import React, { useState } from 'react'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { DashboardCard } from '../components/DashboardCard'

// Mock lessons data
const mockLessonsData = {
  student: {
    name: 'น้องใส',
    student_code: 'STU-001'
  },
  current_week: {
    week_start: '2025-08-18',
    week_end: '2025-08-24',
    sessions: [
      {
        id: 'lesson-001',
        date: '2025-08-18',
        day: 'จันทร์',
        time: '16:00-18:00',
        subjects: [
          {
            name: 'คณิตศาสตร์',
            topic: 'การบวกเลขสามหลัก',
            teacher_notes: 'น้องใสทำแบบฝึกหัดได้ดี เข้าใจการทดแล้ว',
            homework: 'แบบฝึกหัดหน้า 15-16'
          },
          {
            name: 'ภาษาไทย',
            topic: 'การเขียนเรียงความ',
            teacher_notes: 'ฝึกเขียนเรื่อง "ครอบครัวของฉัน" ใช้คำศัพท์ได้ดี',
            homework: 'เขียนเรียงความเรื่อง "วันหยุดที่ฉันชอบ"'
          }
        ],
        attendance: 'present' as const,
        overall_notes: 'น้องใสมีความตั้งใจเรียนดี วันนี้กระตือรือร้นมาก'
      },
      {
        id: 'lesson-002',
        date: '2025-08-20',
        day: 'พุธ',
        time: '16:00-18:00',
        subjects: [
          {
            name: 'ภาษาอังกฤษ',
            topic: 'Family Members',
            teacher_notes: 'ฝึกออกเสียงคำศัพท์ครอบครัว สามารถใช้ประโยคง่ายๆ ได้',
            homework: 'ท่องคำศัพท์ครอบครัว 10 คำ'
          },
          {
            name: 'คณิตศาสตร์',
            topic: 'การลบเลขสามหลัก',
            teacher_notes: 'เริ่มเรียนการยืม ยังต้องฝึกเพิ่ม',
            homework: 'แบบฝึกหัดการลบแบบยืม หน้า 18-19'
          }
        ],
        attendance: 'present' as const,
        overall_notes: 'วันนี้น้องใสเหนื่อยเล็กน้อย แต่ยังทำงานได้ดี'
      },
      {
        id: 'lesson-003',
        date: '2025-08-22',
        day: 'ศุกร์',
        time: '16:00-18:00',
        subjects: [
          {
            name: 'คณิตศาสตร์',
            topic: 'ทบทวนการบวกลบ',
            teacher_notes: 'ทำแบบทดสอบย่อย คะแนน 8/10 ดีมาก',
            homework: 'ทบทวนแบบฝึกหัดเก่า'
          },
          {
            name: 'ภาษาไทย',
            topic: 'การอ่านจับใจความ',
            teacher_notes: 'อ่านนิทานสั้น ตอบคำถามได้ถูก 7/10 ข้อ',
            homework: 'อ่านหนังสือเรื่อง "ลิงกับเต่า"'
          }
        ],
        attendance: 'present' as const,
        overall_notes: 'น้องใสพัฒนาขึ้นเห็นได้ชัด สามารถทำงานได้เร็วขึ้น'
      }
    ]
  },
  upcoming_lessons: [
    {
      date: '2025-08-25',
      day: 'จันทร์',
      time: '16:00-18:00',
      subjects: ['คณิตศาสตร์', 'ภาษาไทย'],
      status: 'scheduled' as const
    },
    {
      date: '2025-08-27',
      day: 'พุธ',
      time: '16:00-18:00',
      subjects: ['ภาษาอังกฤษ', 'คณิตศาสตร์'],
      status: 'scheduled' as const
    }
  ],
  homework_summary: {
    pending: [
      {
        subject: 'คณิตศาสตร์',
        task: 'แบบฝึกหัดการลบแบบยืม หน้า 18-19',
        due_date: '2025-08-25',
        priority: 'medium' as const
      },
      {
        subject: 'ภาษาไทย',
        task: 'เขียนเรียงความเรื่อง "วันหยุดที่ฉันชอบ"',
        due_date: '2025-08-25',
        priority: 'high' as const
      },
      {
        subject: 'ภาษาอังกฤษ',
        task: 'ท่องคำศัพท์ครอบครัว 10 คำ',
        due_date: '2025-08-27',
        priority: 'medium' as const
      }
    ],
    completed: [
      {
        subject: 'คณิตศาสตร์',
        task: 'แบบฝึกหัดหน้า 15-16',
        completed_date: '2025-08-19'
      }
    ]
  }
}

export const LessonsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'recent' | 'upcoming' | 'homework'>('recent')

  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const getSubjectIcon = (subject: string) => {
    switch (subject) {
      case 'คณิตศาสตร์': return '🔢'
      case 'ภาษาไทย': return '📝'
      case 'ภาษาอังกฤษ': return '🇬🇧'
      default: return '📚'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="บทเรียน"
        subtitle={`ติดตามการเรียนของ${mockLessonsData.student.name}`}
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* Tab Navigation */}
        <div className="flex bg-white rounded-lg border p-1">
          <button
            onClick={() => setActiveTab('recent')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'recent'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            📚 บทเรียนล่าสุด
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'upcoming'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            📅 บทเรียนถัดไป
          </button>
          <button
            onClick={() => setActiveTab('homework')}
            className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
              activeTab === 'homework'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            📋 การบ้าน ({mockLessonsData.homework_summary.pending.length})
          </button>
        </div>

        {/* Recent Lessons Tab */}
        {activeTab === 'recent' && (
          <div className="space-y-4">
            <DashboardCard 
              title="📚 บทเรียนสัปดาห์นี้"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
            >
              <div className="space-y-3">
                {mockLessonsData.current_week.sessions.map((lesson) => (
                  <div key={lesson.id} className="bg-white rounded-lg border p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {lesson.day} - {formatThaiDate(lesson.date)}
                        </h3>
                        <p className="text-sm text-gray-600">{lesson.time}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-green-700">เข้าเรียน</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {lesson.subjects.map((subject, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{getSubjectIcon(subject.name)}</span>
                            <span className="font-medium text-sm">{subject.name}</span>
                          </div>
                          <p className="text-xs font-semibold text-gray-900 mb-1">{subject.topic}</p>
                          <p className="text-xs text-gray-600 mb-2">{subject.teacher_notes}</p>
                          {subject.homework && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                              <p className="text-xs text-yellow-800">
                                <span className="font-medium">การบ้าน:</span> {subject.homework}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {lesson.overall_notes && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-800">
                          <span className="font-medium">หมายเหตุ:</span> {lesson.overall_notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Upcoming Lessons Tab */}
        {activeTab === 'upcoming' && (
          <div className="space-y-4">
            <DashboardCard 
              title="📅 บทเรียนที่จะมาถึง"
              className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
            >
              <div className="space-y-3">
                {mockLessonsData.upcoming_lessons.map((lesson, index) => (
                  <div key={index} className="bg-white rounded-lg border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {lesson.day} - {formatThaiDate(lesson.date)}
                        </h3>
                        <p className="text-sm text-gray-600">{lesson.time}</p>
                      </div>
                      <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        กำหนดแล้ว
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {lesson.subjects.map((subject, subIndex) => (
                        <div key={subIndex} className="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1">
                          <span className="text-sm">{getSubjectIcon(subject)}</span>
                          <span className="text-xs font-medium">{subject}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">
                    ตารางเรียนเพิ่มเติมจะแสดงเมื่อโรงเรียนกำหนด
                  </p>
                </div>
              </div>
            </DashboardCard>
          </div>
        )}

        {/* Homework Tab */}
        {activeTab === 'homework' && (
          <div className="space-y-4">
            <DashboardCard 
              title="📋 การบ้านที่ต้องทำ"
              className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200"
            >
              <div className="space-y-3">
                {mockLessonsData.homework_summary.pending.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl mb-2 block">🎉</span>
                    <p className="text-gray-600">ไม่มีการบ้านค้างทำ เก่งมาก!</p>
                  </div>
                ) : (
                  mockLessonsData.homework_summary.pending.map((homework, index) => (
                    <div key={index} className="bg-white rounded-lg border p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getSubjectIcon(homework.subject)}</span>
                          <span className="font-medium">{homework.subject}</span>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(homework.priority)}`}>
                          {homework.priority === 'high' && '🔴 สำคัญ'}
                          {homework.priority === 'medium' && '🟡 ปกติ'}
                          {homework.priority === 'low' && '🟢 ไม่เร่งด่วน'}
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 mb-2">{homework.task}</p>
                      <p className="text-xs text-gray-500">
                        ครบกำหนด: {formatThaiDate(homework.due_date)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </DashboardCard>

            <DashboardCard 
              title="✅ การบ้านที่ทำเสร็จแล้ว"
              className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
            >
              <div className="space-y-3">
                {mockLessonsData.homework_summary.completed.map((homework, index) => (
                  <div key={index} className="bg-white rounded-lg border p-3 opacity-75">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-green-500">✅</span>
                      <span className="text-sm font-medium">{homework.subject}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-1">{homework.task}</p>
                    <p className="text-xs text-gray-500">
                      ทำเสร็จ: {formatThaiDate(homework.completed_date)}
                    </p>
                  </div>
                ))}
              </div>
            </DashboardCard>
          </div>
        )}
      </div>
      
      <TabNavigation />
    </div>
  )
}